'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import dynamic from 'next/dynamic';
import { usePathname, useSelectedLayoutSegments } from 'next/navigation';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BottomNavShell } from '@/app/_components/BottomNavShell';
import { DesktopHeader } from '@/app/_components/DesktopHeader';
import { GlobalModal } from '@/app/_components/GlobalModal';
import { ToastProvider } from '@/app/_components/ToastProvider';
import { ToastSearchParamsController } from '@/app/_components/ToastSearchParamsController';
import { SELECTOR } from '@/constants';
import { RenderedPathnameContext } from '@/hooks/useRenderedPathname';
import { useGlobalStore } from '@/stores/GlobalStore';
import { hasWelloBottomNav } from '@/utils';
import { isDonationPath } from '@/utils/isDonationPath';
import { toRenderedPathname } from '@/utils/toRenderedPathname';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from './constants';
import { GoodsEventTopBand } from './GoodsEventTopBand';
import { ResponsiveDialogContainer } from './ResponsiveDialogContainer';

// SSR 비활성화로 App Router 마운트 이슈 해결
const NaverCtsTracker = dynamic(() => import('./NaverCtsTracker').then(mod => ({
  default: mod.NaverCtsTracker
})), {
  ssr: false
});
interface GlobalLayoutProps {
  children: React.ReactNode;
}
const OptionalGlobalFeatureBoundary = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ErrorBoundary fallback={null}>{children}</ErrorBoundary>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const GlobalLayout = t0 => {
  const $ = _c(33);
  const {
    children
  } = t0;
  const pathname = toRenderedPathname(usePathname() ?? "", useSelectedLayoutSegments());
  const hideBottomNav = useGlobalStore(_temp);
  const showDesktopHeader = !HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname);
  const hasBottomNav = hasWelloBottomNav(pathname, hideBottomNav);
  const T0 = RenderedPathnameContext;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <a href={`#${SELECTOR.MAIN}`} className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-small focus:bg-Gray-black focus:px-16 focus:py-8 focus:font-600 focus:text-Gray-white focus:typo-body4">본문 바로가기</a>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = showDesktopHeader && isDonationPath(pathname) && <GoodsEventTopBand />;
  let t3;
  if ($[1] !== showDesktopHeader) {
    t3 = showDesktopHeader && <DesktopHeader />;
    $[1] = showDesktopHeader;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = showDesktopHeader && "min-h-[calc(100dvh-4.5rem-1px)] mobile-tablet:min-h-dvh";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("relative flex min-h-dvh max-w-full flex-col overflow-x-clip bg-Gray-white", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = SELECTOR;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="sticky top-0 z-20 hidden bg-Gray-white mobile-tablet:block" id={SELECTOR.PAGE_NAV_WRAPPER} />;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <OptionalGlobalFeatureBoundary><ResponsiveDialogContainer /></OptionalGlobalFeatureBoundary>;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const T1 = ToastProvider;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ToastSearchParamsController />;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const t10 = cn("relative flex flex-1 flex-col", {
    "mobile-tablet:pb-[calc(70px+var(--safe-area-bottom,0px))]": hasBottomNav
  });
  let t11;
  if ($[8] !== children) {
    t11 = <Suspense fallback={null}>{children}</Suspense>;
    $[8] = children;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Suspense fallback={null}><OptionalGlobalFeatureBoundary><NaverCtsTracker /></OptionalGlobalFeatureBoundary></Suspense>;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== t10 || $[12] !== t11) {
    t13 = <main className={t10} id={SELECTOR.MAIN}>{t11}{t12}</main>;
    $[11] = t10;
    $[12] = t11;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== T1 || $[15] !== t13 || $[16] !== t9) {
    t14 = <T1>{t9}{t13}</T1>;
    $[14] = T1;
    $[15] = t13;
    $[16] = t9;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <GlobalModal />;
    t16 = <BottomNavShell />;
    $[18] = t15;
    $[19] = t16;
  } else {
    t15 = $[18];
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t14 || $[21] !== t5 || $[22] !== t6.MAIN_WRAPPER || $[23] !== t7 || $[24] !== t8) {
    t17 = <div className={t5} id={t6.MAIN_WRAPPER}>{t7}{t8}{t14}{t15}{t16}</div>;
    $[20] = t14;
    $[21] = t5;
    $[22] = t6.MAIN_WRAPPER;
    $[23] = t7;
    $[24] = t8;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== T0 || $[27] !== pathname || $[28] !== t1 || $[29] !== t17 || $[30] !== t2 || $[31] !== t3) {
    t18 = <T0 value={pathname}>{t1}{t2}{t3}{t17}</T0>;
    $[26] = T0;
    $[27] = pathname;
    $[28] = t1;
    $[29] = t17;
    $[30] = t2;
    $[31] = t3;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  return t18;
};
function _temp(state) {
  return state.hideBottomNav;
}